import React, { useCallback, useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import useText from '../../hooks/useText'
import Text from '../../../ui/Text'
import {
    useScopedAccessGetCurrentScopedAccessContextQuery,
    useScopedAccessStopScopedAccessContextMutation,
} from '../../redux/api/web-api'
import { isFetchBaseQueryError } from '../../redux/api/fetchBaseQueryWithReauth'
import { ConfirmDialog } from '../../uikit/ConfirmDialog'
import { useToast } from '../../uikit/ToastProvider'
import {
    SCOPED_ACCESS_EXPECTED_COMPANY_ID_PARAM,
    SCOPED_ACCESS_RETURN_TO_PARAM,
    addScopedAccessEndedListener,
    broadcastScopedAccessEnded,
    clearScopedAccessExpectedCompanyId,
    clearScopedAccessReturnTo,
    getScopedAccessReturnTo,
    isScopedAccessEndedResponse,
    readScopedAccessExpectedCompanyId,
    readScopedAccessExpectedCompanyIdFromSearch,
    readScopedAccessReturnTo,
    readScopedAccessReturnToFromSearch,
    redirectToStoredScopedAccessReturnTo,
    storeScopedAccessExpectedCompanyId,
    storeScopedAccessReturnTo,
} from '../../utils/scopedAccess'
import { setIsConfirmedScopedAccessActive } from '../../../admin/redux/features/scopedAccessSession/scopedAccessSessionSlice'
import { AppDispatch } from '../../../admin/redux/store'
import { areCompanyIdsEqual } from '../../domain/company-identifiers'

type ScopedAccessSessionStatus = 'pending' | 'active' | 'invalidated' | 'inactive'

export const ScopedAccessBanner = (): React.ReactElement | null => {
    const text = useText()
    const location = useLocation()
    const navigate = useNavigate()
    const toast = useToast()
    const dispatch = useDispatch<AppDispatch>()
    const { data: scopedAccessContext, isSuccess: hasResolvedScopedAccessContextQuery } =
        useScopedAccessGetCurrentScopedAccessContextQuery(undefined, {
            pollingInterval: 60000,
            refetchOnMountOrArgChange: true,
        })
    const [stopScopedAccessContext, { isLoading: isStoppingScopedAccess }] =
        useScopedAccessStopScopedAccessContextMutation()
    const [showInvalidatedSessionDialog, setShowInvalidatedSessionDialog] = useState(false)
    const [hasConfirmedActiveScopedAccessSession, setHasConfirmedActiveScopedAccessSession] = useState(false)
    const activeScopedAccessContext = scopedAccessContext ?? null
    const expectedCompanyId =
        readScopedAccessExpectedCompanyIdFromSearch(location.search) ?? readScopedAccessExpectedCompanyId()
    const hasUnexpectedScopedAccessCompany =
        Boolean(activeScopedAccessContext?.isScopedAccessActive) &&
        Boolean(expectedCompanyId) &&
        !areCompanyIdsEqual(activeScopedAccessContext?.effectiveCompanyId, expectedCompanyId)
    const isScopedAccessSessionActive =
        Boolean(activeScopedAccessContext?.isScopedAccessActive) && !hasUnexpectedScopedAccessCompany
    const hasStoredScopedAccessReturnOrigin = Boolean(readScopedAccessReturnTo())
    const scopedAccessSessionStatus: ScopedAccessSessionStatus = !hasResolvedScopedAccessContextQuery
        ? 'pending'
        : hasUnexpectedScopedAccessCompany
          ? 'invalidated'
          : isScopedAccessSessionActive
            ? 'active'
            : hasConfirmedActiveScopedAccessSession && hasStoredScopedAccessReturnOrigin
              ? 'invalidated'
              : 'inactive'

    const redirectToStoredReturnOriginOrNotifyError = useCallback(() => {
        try {
            getScopedAccessReturnTo()
            redirectToStoredScopedAccessReturnTo()
            return true
        } catch {
            toast.notifyError(text('msgScopedAccessMissingReturnOrigin'))
            return false
        }
    }, [text, toast])

    // Consume one-time bootstrap params, persist them for this tab, and remove them from the URL.
    useEffect(() => {
        const returnTo = readScopedAccessReturnToFromSearch(location.search)
        const expectedCompanyIdFromSearch = readScopedAccessExpectedCompanyIdFromSearch(location.search)
        if (!returnTo && !expectedCompanyIdFromSearch) {
            return
        }

        if (returnTo) {
            storeScopedAccessReturnTo(returnTo)
        }
        if (expectedCompanyIdFromSearch) {
            storeScopedAccessExpectedCompanyId(expectedCompanyIdFromSearch)
        }

        const searchParams = new URLSearchParams(location.search)
        searchParams.delete(SCOPED_ACCESS_RETURN_TO_PARAM)
        searchParams.delete(SCOPED_ACCESS_EXPECTED_COMPANY_ID_PARAM)

        navigate(
            {
                pathname: location.pathname,
                search: searchParams.toString() ? `?${searchParams.toString()}` : '',
            },
            { replace: true }
        )
    }, [location.pathname, location.search, navigate])

    // Model the scoped-access lifecycle explicitly so active, invalidated, and stale-bootstrap cases stay distinct.
    useEffect(() => {
        switch (scopedAccessSessionStatus) {
            case 'active':
                dispatch(setIsConfirmedScopedAccessActive(true))
                setHasConfirmedActiveScopedAccessSession(true)
                setShowInvalidatedSessionDialog(false)
                return
            case 'invalidated':
                dispatch(setIsConfirmedScopedAccessActive(false))
                setShowInvalidatedSessionDialog(true)
                return
            case 'inactive':
                clearScopedAccessReturnTo()
                clearScopedAccessExpectedCompanyId()
                dispatch(setIsConfirmedScopedAccessActive(false))
                return
            case 'pending':
            default:
                return
        }
    }, [dispatch, scopedAccessSessionStatus])

    // React immediately when another tab ends or replaces scoped access, instead of waiting for polling.
    useEffect(
        () =>
            addScopedAccessEndedListener(() => {
                dispatch(setIsConfirmedScopedAccessActive(false))
                setShowInvalidatedSessionDialog(true)
            }),
        [dispatch]
    )

    const handleExitSession = async () => {
        try {
            await stopScopedAccessContext().unwrap()
            dispatch(setIsConfirmedScopedAccessActive(false))
            broadcastScopedAccessEnded()
            clearScopedAccessExpectedCompanyId()
            redirectToStoredReturnOriginOrNotifyError()
        } catch (error) {
            if (isFetchBaseQueryError(error) && isScopedAccessEndedResponse(error)) {
                dispatch(setIsConfirmedScopedAccessActive(false))
                setShowInvalidatedSessionDialog(true)
                return
            }

            toast.notifyError(text('msgCouldNotExitScopedAccessSession'))
        }
    }

    if (showInvalidatedSessionDialog) {
        return (
            <ConfirmDialog
                visible={showInvalidatedSessionDialog}
                testId="scoped-access-invalidated-dialog"
                header={text('msgScopedAccessSessionNoLongerValid')}
                message={text('msgScopedAccessSessionNoLongerValidDescription')}
                icon="pi pi-exclamation-triangle"
                closable={false}
                dismissableMask={false}
                onHide={() => undefined}
                footer={() => (
                    <div className="flex justify-content-end">
                        <Button
                            type="button"
                            label={text('btnGoBack')}
                            onClick={redirectToStoredReturnOriginOrNotifyError}
                            data-testid="btn-go-back-invalid-scoped-access-session"
                        />
                    </div>
                )}
            />
        )
    }

    if (!isScopedAccessSessionActive) {
        return null
    }

    return (
        <div
            className="w-full h-3rem m-0 border-bottom-1 border-blue-100 bg-blue-50 text-blue-800 flex align-items-center gap-3 px-3 justify-content-between"
            data-testid="scoped-access-banner"
        >
            <span data-testid="scoped-access-banner-message">
                <Text
                    code="msgScopedAccessBanner"
                    values={{
                        companyName:
                            activeScopedAccessContext?.effectiveScopeName ?? text('lblThisOrganisationFallback'),
                    }}
                />
            </span>

            <Button
                type="button"
                size="small"
                outlined
                className="border-blue-800 text-blue-800"
                loading={isStoppingScopedAccess}
                label={text('btnExitScopedAccessSession')}
                onClick={() => void handleExitSession()}
                data-testid="btn-exit-scoped-access-session"
            />
        </div>
    )
}
